import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import getColumns from './Components/TableColumns';
import LoadingSpinner from '../../../_GlobalComponents/LoadingSpinner';
import TableHeaderActions from './Components/TableHeaderActions';
import OrderConfirmModal from '../OrderConfirmModal/OrderConfirmModal';
import RowDetailModal from './Components/RowDetailModal';
import RecentOrderInfoModal from './Components/RecentOrderInfoModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSmartOrderCart,
  addToSmartOrderCart,
  deleteFromSmartOrderCart,
  clearSmartOrderCart,
  updateCartItemQuantity,
} from '../../../../Redux/SmartOrderCartSlice';
import { resolveCustomerToken } from '../../utils/customerToken';

const TOP_FACET_KEYS = ['top_facet_1', 'top_facet_2', 'top_facet_3', 'top_facet_4', 'top_facet_5'];
const ALPHANUMERIC_SORTER = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const isNumericLeadingValue = (value) => /^\d/.test(String(value).trim());

const compareFacetValues = (a, b) => {
  const aNumericFirst = isNumericLeadingValue(a);
  const bNumericFirst = isNumericLeadingValue(b);

  if (aNumericFirst && !bNumericFirst) return -1;
  if (!aNumericFirst && bNumericFirst) return 1;

  return ALPHANUMERIC_SORTER.compare(String(a), String(b));
};

const SuggestedOrderTable = ({
  onOpenHelp,
  onTableLoaded,
  onPersistAddItem,
  onPersistEditItem,
  onPersistRemoveItem,
  onPersistClearCart,
  onPersistRebuildCart,
  webOrderId,
}) => {
  const normalizeFacetEntry = useCallback((entry) => {
    if (!entry) return null;
    if (typeof entry === 'object') {
      const [facetName, facetValue] = Object.entries(entry)[0] || [];
      if (!facetName) return null;
      const valueText =
        facetValue === null || facetValue === undefined ? '' : String(facetValue).trim();
      return {
        name: String(facetName).trim(),
        value: valueText,
      };
    }
    if (typeof entry === 'string') {
      const trimmed = entry.trim();
      if (!trimmed) return null;
      const [facetName, ...rest] = trimmed.split(':');
      const parsedName = String(facetName || '').trim();
      const parsedValue = String(rest.join(':') || '').trim();
      return {
        name: parsedName || trimmed,
        value: parsedValue,
      };
    }
    return null;
  }, []);

  const getRowFacetPairs = useCallback(
    (row) =>
      TOP_FACET_KEYS
        .map((key) => normalizeFacetEntry(row?.[key]))
        .filter((value) => Boolean(value?.name)),
    [normalizeFacetEntry],
  );

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [token, setToken] = useState('');
  const [dataRefreshToggle, setDataRefreshToggle] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItemNumber, setSelectedItemNumber] = useState(null);
  const [message, setMessage] = useState('');
  const [messageColor, setMessageColor] = useState('#000');
  const [userPersonalization, setUserPersonalization] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFacetFilters, setSelectedFacetFilters] = useState({});
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [minMaxEditOpen, setMinMaxEditOpen] = useState(false);
  const [editingItemNumber, setEditingItemNumber] = useState(null);
  const [editingMinQty, setEditingMinQty] = useState('');
  const [editingMaxQty, setEditingMaxQty] = useState('');
  const [isSavingMinMax, setIsSavingMinMax] = useState(false);
  const [stopRecommendConfirmOpen, setStopRecommendConfirmOpen] = useState(false);
  const [recentOrderModalOpen, setRecentOrderModalOpen] = useState(false);
  const [recentOrderLoading, setRecentOrderLoading] = useState(false);
  const [recentOrderError, setRecentOrderError] = useState('');
  const [recentOrderItemNumber, setRecentOrderItemNumber] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const quickSearchRef = useRef(null);
  const hasQuickFilterValueRef = useRef(false);

  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedRows = useSelector((state) => state.smartOrderCart.value);

  useEffect(() => {
    hasQuickFilterValueRef.current = Boolean(filterModel.quickFilterValues?.length);
  }, [filterModel.quickFilterValues]);

  useEffect(() => {
    const resolvedToken = resolveCustomerToken();
    setToken(resolvedToken);
    setIsLoading(true);

    axios
      .post('/suggestedOrders', {
        customerNumber: resolvedToken,
      })
      .then((response) => {
        const { result, message, message_color, details, userId } = response.data;

        setMessage(message);
        setMessageColor(message_color || '#000');

        if (result === 'success' && details) {
          const dataWithCheckbox = details.map((item) => ({ ...item, isChecked: false }));
          setData(dataWithCheckbox);
        } else {
          setData([]);
        }

        if (userId) {
          axios
            .post('/userPersonalization/getbyroute', {
              domain: 'TSW',
              route: 'suggested-orders',
              userId,
            })
            .then((userResponse) => {
              setUserPersonalization(userResponse.data);
            })
            .catch((error) => {
              console.error('Error fetching user personalization:', error);
            });
        }

        setIsLoading(false);
        onTableLoaded?.();
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setIsLoading(false);
        onTableLoaded?.();
      });
  }, [dataRefreshToggle, onTableLoaded]);

  const handleCheckChange = useCallback(
    (row, isChecked) => {
      const updatedData = data.map((item) =>
        item.item_number === row.item_number ? { ...item, isChecked } : item,
      );
      setData(updatedData);

      if (isChecked) {
        dispatch(addToSmartOrderCart({ ...row, line_source: 'smart_order' }));
        onPersistAddItem?.({
          ...row,
          line_source: 'smart_order',
          suggested_qty: Number(row.suggested_qty || 1),
        });
      } else {
        dispatch(deleteFromSmartOrderCart(row.item_number));
        onPersistRemoveItem?.(row.item_number);
      }
    },
    [data, dispatch, onPersistAddItem, onPersistRemoveItem],
  );

  const handleAddOrRemoveCart = useCallback(
    (row, shouldAdd) => {
      handleCheckChange(row, shouldAdd);
    },
    [handleCheckChange],
  );

  useEffect(() => {
    if (!isLoading) {
      setData((prevData) =>
        prevData.map((item) => {
          const cartItem = selectedRows.find(
            (cartItem) => cartItem.item_number === item.item_number,
          );
          return cartItem
            ? { ...item, isChecked: true, suggested_qty: cartItem.suggested_qty }
            : { ...item, isChecked: false };
        }),
      );
    }
  }, [selectedRows, isLoading]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleItemOpenModal = useCallback((itemNumber) => {
    setSelectedItemNumber(itemNumber);
    setModalOpen(true);
  }, []);

  const handleItemCloseModal = () => {
    setModalOpen(false);
    setSelectedItemNumber(null);
  };

  const handleOrderPlaced = () => {
    dispatch(clearSmartOrderCart());
    setData([]);
    setDataRefreshToggle((prev) => !prev);
    onPersistClearCart?.();
  };

  const handleOpenRecentOrderModal = useCallback(
    async (itemNumber) => {
      if (!itemNumber) {
        return;
      }

      setRecentOrderModalOpen(true);
      setRecentOrderItemNumber(itemNumber);
      setRecentOrderLoading(true);
      setRecentOrderError('');
      setRecentOrders([]);

      try {
        const response = await axios.post('/suggestedOrders/getRecentOrderInfo', {
          customerNumber: token,
          itemNumber,
        });

        const payload = response?.data || {};
        const orders = Array.isArray(payload.recent_orders) ? payload.recent_orders : [];
        const isSuccess =
          payload.result === 'success' || (payload.result === undefined && Array.isArray(payload.recent_orders));

        if (!isSuccess) {
          setRecentOrderError(
            payload.message ||
              payload.error ||
              'Unable to load recent order details.',
          );
          setRecentOrders([]);
          return;
        }

        setRecentOrders(orders);
      } catch (error) {
        console.error('Error fetching recent order details:', error);
        const backendMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data === 'string' ? error.response.data : '');
        setRecentOrderError(
          backendMessage || 'Failed to load recent order details. Please try again.',
        );
      } finally {
        setRecentOrderLoading(false);
      }
    },
    [token],
  );

  const handleCloseRecentOrderModal = useCallback(() => {
    setRecentOrderModalOpen(false);
  }, []);

  const handleRecentOrderModalExited = useCallback(() => {
    setRecentOrderLoading(false);
    setRecentOrderError('');
    setRecentOrderItemNumber(null);
    setRecentOrders([]);
  }, []);

  const handleQtyChange = useCallback(
    (item_number, rawNewQty) => {
      const newQuantity = parseInt(rawNewQty, 10);

      const baseItemData = data.find((item) => item.item_number === item_number);
      if (!baseItemData) {
        console.error('Could not find item in local data:', item_number);
        return;
      }

      if (isNaN(newQuantity) || newQuantity < 1) {
        const isInCart = selectedRows.some((cartItem) => cartItem.item_number === item_number);
        if (isInCart) {
          dispatch(deleteFromSmartOrderCart(item_number));
        }

        setData((currentData) =>
          currentData.map((item) =>
            item.item_number === item_number
              ? { ...item, suggested_qty: baseItemData.suggested_qty || '' }
              : item,
          ),
        );
        return;
      }

      setData((currentData) =>
        currentData.map((item) =>
          item.item_number === item_number ? { ...item, suggested_qty: newQuantity } : item,
        ),
      );

      const isInCart = selectedRows.some((cartItem) => cartItem.item_number === item_number);

      if (isInCart) {
        dispatch(updateCartItemQuantity({ item_number, newQuantity }));
        onPersistEditItem?.(item_number, newQuantity);
      }
    },
    [data, dispatch, onPersistEditItem, selectedRows],
  );

  const handleStepperChange = useCallback(
    (row, delta) => {
      setQtyDrafts((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, row.item_number)) {
          return prev;
        }
        const next = { ...prev };
        delete next[row.item_number];
        return next;
      });
      const cartItem = selectedRows.find((item) => item.item_number === row.item_number);
      const currentQty = cartItem ? Number(cartItem.suggested_qty) : Number(row.suggested_qty || 1);
      const nextQty = currentQty + delta;
      handleQtyChange(row.item_number, String(nextQty));
    },
    [handleQtyChange, selectedRows],
  );

  const handleClearSelection = useCallback(() => {
    const updatedData = data.map((item) => ({ ...item, isChecked: false }));
    setData(updatedData);
    dispatch(clearSmartOrderCart());
    onPersistClearCart?.();
  }, [data, dispatch, onPersistClearCart]);

  const manufacturers = useMemo(() => {
    const filtered =
      selectedType === 'All' || selectedType === ''
        ? data
        : data.filter((item) => item.item_type === selectedType);
    return Array.from(
      new Set(filtered.map((item) => item.manufacturer_name).filter(Boolean)),
    ).sort();
  }, [data, selectedType]);

  const types = useMemo(() => {
    const filtered =
      selectedManufacturer === 'All' || selectedManufacturer === ''
        ? data
        : data.filter((item) => item.manufacturer_name === selectedManufacturer);
    return Array.from(new Set(filtered.map((item) => item.item_type).filter(Boolean))).sort();
  }, [data, selectedManufacturer]);

  const categories = useMemo(() => {
    const filtered = data.filter((item) => {
      const manufacturerMatches =
        selectedManufacturer === 'All' ||
        selectedManufacturer === '' ||
        item.manufacturer_name === selectedManufacturer;
      const typeMatches =
        selectedType === 'All' || selectedType === '' || item.item_type === selectedType;
      return manufacturerMatches && typeMatches;
    });

    return Array.from(new Set(filtered.map((item) => item.category).filter(Boolean))).sort();
  }, [data, selectedManufacturer, selectedType]);

  const getRowFacetMap = useCallback(
    (row) =>
      getRowFacetPairs(row).reduce((acc, facet) => {
        acc[facet.name] = facet.value;
        return acc;
      }, {}),
    [getRowFacetPairs],
  );

  const baseFilteredRows = useMemo(() => {
    return data.filter((item) => {
      const manufacturerMatches =
        selectedManufacturer === 'All' ||
        selectedManufacturer === '' ||
        item.manufacturer_name === selectedManufacturer;
      const typeMatches =
        selectedType === 'All' || selectedType === '' || item.item_type === selectedType;
      const categoryMatches =
        selectedCategory === 'All' || selectedCategory === '' || item.category === selectedCategory;
      return manufacturerMatches && typeMatches && categoryMatches;
    });
  }, [data, selectedCategory, selectedManufacturer, selectedType]);

  const facetNames = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '') {
      return [];
    }

    const seen = new Set();
    const orderedNames = [];
    const categoryItems = data.filter((item) => item.category === selectedCategory);

    categoryItems.forEach((item) => {
      TOP_FACET_KEYS.forEach((key) => {
        const facet = normalizeFacetEntry(item?.[key]);
        if (facet?.name && !seen.has(facet.name)) {
          seen.add(facet.name);
          orderedNames.push(facet.name);
        }
      });
    });

    return orderedNames;
  }, [data, normalizeFacetEntry, selectedCategory]);

  const facetOptionsByName = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      return {};
    }

    return facetNames.reduce((acc, facetName) => {
      const rowsMatchingOtherFacetSelections = baseFilteredRows.filter((row) => {
        const facetMap = getRowFacetMap(row);
        return facetNames.every((name) => {
          if (name === facetName) return true;
          const selectedValue = selectedFacetFilters[name];
          if (!selectedValue) return true;
          return facetMap[name] === selectedValue;
        });
      });

      const options = Array.from(
        new Set(
          rowsMatchingOtherFacetSelections
            .map((row) => getRowFacetMap(row)[facetName])
            .filter(Boolean),
        ),
      ).sort(compareFacetValues);

      acc[facetName] = options;
      return acc;
    }, {});
  }, [baseFilteredRows, facetNames, getRowFacetMap, selectedCategory, selectedFacetFilters]);

  useEffect(() => {
    if (!manufacturers.length) return;
    if (selectedManufacturer !== 'All' && !manufacturers.includes(selectedManufacturer)) {
      setSelectedManufacturer('All');
    }
  }, [manufacturers, selectedManufacturer]);

  useEffect(() => {
    if (!types.length) return;
    if (selectedType !== 'All' && !types.includes(selectedType)) {
      setSelectedType('All');
    }
  }, [types, selectedType]);

  useEffect(() => {
    if (!categories.length) return;
    if (selectedCategory !== 'All' && !categories.includes(selectedCategory)) {
      setSelectedCategory('All');
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      setSelectedFacetFilters({});
      return;
    }

    setSelectedFacetFilters((prev) => {
      const next = {};
      facetNames.forEach((name) => {
        if (prev[name]) {
          next[name] = prev[name];
        }
      });
      return next;
    });
  }, [facetNames, selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      return;
    }

    setSelectedFacetFilters((prev) => {
      let changed = false;
      const next = { ...prev };

      facetNames.forEach((name) => {
        const selectedValue = next[name];
        if (!selectedValue) return;
        const options = facetOptionsByName[name] || [];
        if (!options.includes(selectedValue)) {
          delete next[name];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [facetNames, facetOptionsByName, selectedCategory]);

  const setFacetFilter = useCallback((facetName, value) => {
    setSelectedFacetFilters((prev) => {
      if (!value) {
        if (!prev[facetName]) return prev;
        const next = { ...prev };
        delete next[facetName];
        return next;
      }
      if (prev[facetName] === value) return prev;
      return {
        ...prev,
        [facetName]: value,
      };
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setSelectedManufacturer('All');
    setSelectedType('All');
    setSelectedCategory('All');
    setSelectedFacetFilters({});
    setFilterModel({ items: [], quickFilterValues: [] });
  }, []);

  const filteredData = useMemo(() => {
    return baseFilteredRows.filter((item) => {
      const rowFacetMap = getRowFacetMap(item);
      return facetNames.every((name) => {
        const selectedValue = selectedFacetFilters[name];
        if (!selectedValue) return true;
        return rowFacetMap[name] === selectedValue;
      });
    });
  }, [baseFilteredRows, facetNames, getRowFacetMap, selectedFacetFilters]);

  const handleSelectAll = useCallback(() => {
    const filteredItems = filteredData.map((item) => ({
      ...item,
      isChecked: true,
      line_source: 'smart_order',
    }));
    setData((prevData) =>
      prevData.map((item) => {
        const match = filteredItems.find((f) => f.item_number === item.item_number);
        return match ? { ...item, isChecked: true } : item;
      }),
    );
    dispatch(setSmartOrderCart(filteredItems));
    onPersistRebuildCart?.(filteredItems);
  }, [dispatch, filteredData, onPersistRebuildCart]);

  const tableColumns = useMemo(
    () =>
      getColumns({
        handleItemOpenModal,
        handleLastOrderedClick: handleOpenRecentOrderModal,
        onOpenHelp,
        theme,
        userPersonalization,
      }),
    [handleItemOpenModal, handleOpenRecentOrderModal, onOpenHelp, theme, userPersonalization],
  );

  const openMinMaxEditModal = useCallback((row) => {
    setEditingItemNumber(row.item_number);
    setEditingMinQty(String(row.min_quantity ?? row.item_min ?? 0));
    setEditingMaxQty(String(row.max_quantity ?? row.item_max ?? 0));
    setMinMaxEditOpen(true);
  }, []);

  const handleCloseMinMaxModal = useCallback(() => {
    if (!isSavingMinMax) {
      setMinMaxEditOpen(false);
      setEditingItemNumber(null);
      setEditingMinQty('');
      setEditingMaxQty('');
    }
  }, [isSavingMinMax]);

  const handleSaveMinMax = useCallback(async () => {
    const parsedMin = parseFloat(editingMinQty);
    const parsedMax = parseFloat(editingMaxQty);

    if (Number.isNaN(parsedMin) || Number.isNaN(parsedMax) || parsedMin > parsedMax) {
      return;
    }

    setIsSavingMinMax(true);
    try {
      await axios.post('/suggestedOrders/addUpdateItem', {
        customerNumber: token,
        itemNumber: editingItemNumber,
        itemMin: parsedMin,
        itemMax: parsedMax,
      });

      setData((prev) =>
        prev.map((item) =>
          item.item_number === editingItemNumber
            ? { ...item, min_quantity: parsedMin, max_quantity: parsedMax }
            : item,
        ),
      );
      handleCloseMinMaxModal();
    } catch (error) {
      console.error('Error saving min/max values:', error);
    } finally {
      setIsSavingMinMax(false);
    }
  }, [editingItemNumber, editingMaxQty, editingMinQty, handleCloseMinMaxModal, token]);

  const handleStopRecommending = useCallback(async () => {
    const parsedMin = parseFloat(editingMinQty);
    const parsedMax = parseFloat(editingMaxQty);

    if (Number.isNaN(parsedMin) || Number.isNaN(parsedMax) || parsedMin > parsedMax) {
      return;
    }

    setIsSavingMinMax(true);
    try {
      await axios.post('/suggestedOrders/addUpdateItem', {
        customerNumber: token,
        itemNumber: editingItemNumber,
        itemMin: parsedMin,
        itemMax: parsedMax,
        h: true,
      });

      dispatch(deleteFromSmartOrderCart(editingItemNumber));
      const hiddenItemNumber = String(editingItemNumber);
      setData((prev) => prev.filter((item) => String(item.item_number) !== hiddenItemNumber));
      setStopRecommendConfirmOpen(false);
      handleCloseMinMaxModal();
    } catch (error) {
      console.error('Error stopping recommendations for item:', error);
    } finally {
      setIsSavingMinMax(false);
    }
  }, [
    dispatch,
    editingItemNumber,
    editingMaxQty,
    editingMinQty,
    handleCloseMinMaxModal,
    token,
  ]);

  const hasInvalidMinMaxRange = useMemo(() => {
    const parsedMin = parseFloat(editingMinQty);
    const parsedMax = parseFloat(editingMaxQty);
    if (Number.isNaN(parsedMin) || Number.isNaN(parsedMax)) {
      return false;
    }
    return parsedMin > parsedMax;
  }, [editingMaxQty, editingMinQty]);

  const columns = useMemo(
    () => [
      {
        field: 'action_stack',
        headerName: '',
        width: 210,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          const cartItem = selectedRows.find((item) => item.item_number === row.item_number);
          const isInCart = Boolean(cartItem);
          const currentQty = cartItem
            ? Number(cartItem.suggested_qty)
            : Number(row.suggested_qty || 1);
          const draftQty = qtyDrafts[row.item_number];
          const displayQty = draftQty !== undefined ? draftQty : String(currentQty);
          const minValue = row.min_quantity ?? row.item_min ?? 0;
          const maxValue = row.max_quantity ?? row.item_max ?? 0;

          return (
            <Stack spacing={0.4} sx={{ width: 'auto', py: 0.25 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: 'lightgrey',
                  borderRadius: 1.5,
                  p: 0.8,
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderWidth: 0,
                    backgroundColor: 'rgba(255,255,255,.6)',
                    borderRadius: 0.75,
                    px: 0.5,
                    width: 92,
                    height: 30,
                    flex: '0 0 auto',
                  }}
                >
                  <IconButton
                    size='small'
                    sx={{
                      p: 0.25,
                      color: '#fff',
                      backgroundColor: 'rgba(000,000,000,0.2)',
                      borderRadius: 0.75,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.35)' },
                    }}
                    onClick={() => handleStepperChange(row, -1)}
                  >
                    <RemoveIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Box
                    component='input'
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={displayQty}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      if (!/^\d*$/.test(rawValue)) {
                        return;
                      }

                      setQtyDrafts((prev) => ({
                        ...prev,
                        [row.item_number]: rawValue,
                      }));

                      if (rawValue !== '') {
                        handleQtyChange(row.item_number, rawValue);
                      }
                    }}
                    onBlur={() => {
                      setQtyDrafts((prev) => {
                        if (!Object.prototype.hasOwnProperty.call(prev, row.item_number)) {
                          return prev;
                        }

                        const rawValue = prev[row.item_number];
                        const next = { ...prev };

                        if (rawValue === '') {
                          delete next[row.item_number];
                          return next;
                        }

                        const parsedValue = parseInt(rawValue, 10);
                        if (Number.isNaN(parsedValue)) {
                          delete next[row.item_number];
                          return next;
                        }

                        const normalizedValue = String(parsedValue);
                        if (normalizedValue !== rawValue) {
                          next[row.item_number] = normalizedValue;
                        } else {
                          delete next[row.item_number];
                        }

                        return next;
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      fontWeight: 700,
                      minWidth: 24,
                      width: 24,
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      color: theme.palette.primary.main,
                      cursor: 'text',
                      background: 'transparent',
                      border: 0,
                      outline: 0,
                      p: 0,
                      MozAppearance: 'textfield',
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    }}
                  />
                  <IconButton
                    size='small'
                    sx={{
                      p: 0.25,
                      color: '#fff',
                      backgroundColor: 'rgba(000,000,000,0.2)',
                      borderRadius: 0.75,
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.35)' },
                    }}
                    onClick={() => handleStepperChange(row, 1)}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Button
                  size='small'
                  variant='contained'
                  color='inherit'
                  onClick={() => handleAddOrRemoveCart(row, !isInCart)}
                  sx={{
                    minWidth: 30,
                    width: 30,
                    height: 30,
                    p: 0,
                    color: '#fff',
                    backgroundColor: isInCart ? '#ff6b6b' : theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: isInCart ? '#e25555' : theme.palette.primary.dark,
                    },
                  }}
                  aria-label={isInCart ? 'Remove from cart' : 'Add to cart'}
                >
                  {isInCart ? (
                    <DeleteOutlineIcon sx={{ fontSize: 16, color: 'white' }} />
                  ) : (
                    <AddShoppingCartIcon sx={{ fontSize: 16 }} />
                  )}
                </Button>
              </Box>

              <Box
                onClick={() => openMinMaxEditModal(row)}
                sx={{
                  cursor: 'pointer',
                  color: '#1976d2',
                  textDecoration: 'underline',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  width: '100%',
                  textAlign: 'center',
                  // textTransform: 'uppercase',
                }}
              >
                {`Min: ${minValue} / Max: ${maxValue}`}
              </Box>
            </Stack>
          );
        },
      },
      ...tableColumns,
    ],
    [
      handleAddOrRemoveCart,
      handleQtyChange,
      handleStepperChange,
      openMinMaxEditModal,
      qtyDrafts,
      selectedRows,
      tableColumns,
      theme,
    ],
  );

  const QuickSearchToolbar = useCallback(
    () => (
      <GridToolbarContainer
        sx={{
          px: 0.75,
          py: 0.75,
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 0.75,
          backgroundColor: theme.palette.primary.main,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0.75,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <TableHeaderActions
            selectedRows={selectedRows}
            handleClearSelection={handleClearSelection}
            handleSelectAll={handleSelectAll}
            data={data}
            manufacturers={manufacturers}
            selectedManufacturer={selectedManufacturer}
            setSelectedManufacturer={setSelectedManufacturer}
            types={types}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            facetNames={facetNames}
            facetOptionsByName={facetOptionsByName}
            selectedFacetFilters={selectedFacetFilters}
            setFacetFilter={setFacetFilter}
            onClearAllFilters={handleClearAllFilters}
          />
        </Box>
        <Box sx={{ position: 'relative', width: 260, height: 34, ml: 'auto' }}>
          <Box
            sx={{
              position: 'absolute',
              right: 36,
              top: 0,
              width: 220,
              transformOrigin: 'right center',
              transform: isQuickSearchOpen
                ? 'translateX(0) scaleX(1)'
                : 'translateX(10px) scaleX(0.85)',
              opacity: isQuickSearchOpen ? 1 : 0,
              pointerEvents: isQuickSearchOpen ? 'auto' : 'none',
              transition: 'transform 220ms ease, opacity 220ms ease',
            }}
          >
            <GridToolbarQuickFilter
              inputRef={quickSearchRef}
              debounceMs={300}
              placeholder='Search'
              onBlur={() => {
                if (!hasQuickFilterValueRef.current) {
                  setIsQuickSearchOpen(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && !hasQuickFilterValueRef.current) {
                  setIsQuickSearchOpen(false);
                }
              }}
              InputProps={{ startAdornment: null }}
              sx={{
                width: 220,
                pb: 0,
                '& input': {
                  color: theme.palette.common.white,
                },
                '& input::placeholder': {
                  color: theme.palette.common.white,
                  opacity: 0.9,
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiInput-underline:hover:before': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.common.white,
                },
              }}
            />
          </Box>
          <IconButton
            aria-label='search'
            onClick={() => {
              if (!isQuickSearchOpen) {
                setIsQuickSearchOpen(true);
                setTimeout(() => {
                  quickSearchRef.current?.focus();
                }, 0);
                return;
              }
              if (!hasQuickFilterValueRef.current) {
                setIsQuickSearchOpen(false);
              }
            }}
            sx={{ position: 'absolute', right: 0, top: 0, color: theme.palette.common.white }}
          >
            <SearchIcon fontSize='small' />
          </IconButton>
        </Box>
      </GridToolbarContainer>
    ),
    [
      data,
      handleClearSelection,
      handleSelectAll,
      isQuickSearchOpen,
      manufacturers,
      selectedManufacturer,
      selectedRows,
      selectedFacetFilters,
      selectedType,
      theme.palette.common.white,
      theme.palette.primary.main,
      facetNames,
      types,
      categories,
      facetOptionsByName,
      selectedCategory,
      setFacetFilter,
      handleClearAllFilters,
    ],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        minHeight: 0,
        flexGrow: 1,
        p: 2,
        overflow: 'hidden',
      }}
    >
      <LoadingSpinner isLoading={isLoading} />

      <Box sx={{ mb: 1 }}>
        <Typography
          variant='body1'
          sx={{ color: messageColor, fontSize: '1.1rem', textAlign: 'center' }}
        >
          {message}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row.item_number}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 88}
          disableRowSelectionOnClick
          showToolbar
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'last_ordered_days', sort: 'asc' }],
            },
            columns: {
              columnVisibilityModel: {
                item_number: false,
              },
            },
            density: 'compact',
            filter: {
              filterModel: {
                items: [],
                quickFilterExcludeHiddenColumns: true,
              },
            },
          }}
          slots={{ toolbar: QuickSearchToolbar }}
          localeText={{
            noRowsLabel: isLoading ? '' : 'No Records To Display',
          }}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: '8px',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme.palette.grey[100],
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '1rem',
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              py: 0.5,
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell[data-field="dealer_quantity"]': {
              p: '2px !important',
            },
            '& .MuiDataGrid-cell[data-field="last_ordered_days"]': {
              p: '2px !important',
            },
            '& .MuiDataGrid-toolbarContainer': {
              px: 0.75,
              py: 0.75,
            },
          }}
        />
      </Box>

      {isModalOpen && (
        <OrderConfirmModal
          handleClose={handleCloseModal}
          customerNumber={token}
          webOrderId={webOrderId}
          handleOrderPlaced={handleOrderPlaced}
          onPersistRemoveItem={onPersistRemoveItem}
          onPersistQuantityChange={onPersistEditItem}
        />
      )}
      <RowDetailModal
        open={modalOpen}
        handleClose={handleItemCloseModal}
        itemNumber={selectedItemNumber}
      />
      <RecentOrderInfoModal
        open={recentOrderModalOpen}
        onClose={handleCloseRecentOrderModal}
        onExited={handleRecentOrderModalExited}
        loading={recentOrderLoading}
        error={recentOrderError}
        itemNumber={recentOrderItemNumber}
        orders={recentOrders}
      />
      <Dialog
        open={minMaxEditOpen}
        onClose={handleCloseMinMaxModal}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.primary.main}2e`,
            boxShadow: `0 14px 34px ${theme.palette.primary.main}1f`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 34%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.2,
            borderBottom: `1px solid ${theme.palette.primary.main}26`,
            backgroundColor: `${theme.palette.primary.main}12`,
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Edit Min/Max
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Update quantity thresholds for this item.
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers={false}
          sx={{
            '&.MuiDialogContent-root': {
              pt: '24px !important',
              pb: '14px',
            },
          }}
        >
          <Stack spacing={2}>
            <TextField
              label='Min Qty'
              type='number'
              size='small'
              value={editingMinQty}
              onChange={(e) => setEditingMinQty(e.target.value)}
              error={hasInvalidMinMaxRange}
              sx={{
                '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper },
              }}
            />
            <TextField
              label='Max Qty'
              type='number'
              size='small'
              value={editingMaxQty}
              onChange={(e) => setEditingMaxQty(e.target.value)}
              error={hasInvalidMinMaxRange}
              helperText={hasInvalidMinMaxRange ? 'Max Qty cannot be less than Min Qty.' : ''}
              sx={{
                '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.background.paper },
              }}
            />
            <Box
              onClick={() => setStopRecommendConfirmOpen(true)}
              sx={{
                alignSelf: 'flex-start',
                cursor: isSavingMinMax ? 'default' : 'pointer',
                color: isSavingMinMax ? 'text.disabled' : theme.palette.primary.main,
                textDecoration: 'underline',
                fontSize: '0.9rem',
                fontWeight: 600,
                pointerEvents: isSavingMinMax ? 'none' : 'auto',
              }}
            >
              Stop Recommending This Item
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.primary.main}22`,
            backgroundColor: `${theme.palette.primary.main}08`,
          }}
        >
          <Button
            onClick={handleCloseMinMaxModal}
            disabled={isSavingMinMax}
            sx={{ color: theme.palette.delete.main }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveMinMax}
            variant='contained'
            disabled={isSavingMinMax || hasInvalidMinMaxRange}
            sx={{ minWidth: 100, fontWeight: 700 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stopRecommendConfirmOpen}
        onClose={() => !isSavingMinMax && setStopRecommendConfirmOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.primary.main}2e`,
            boxShadow: `0 14px 34px ${theme.palette.primary.main}1f`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 34%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.2,
            borderBottom: `1px solid ${theme.palette.primary.main}26`,
            backgroundColor: `${theme.palette.primary.main}12`,
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Stop Recommending Item
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            This item will be hidden from Smart Order recommendations.
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            '&.MuiDialogContent-root': {
              pt: '18px !important',
            },
          }}
        >
          <Typography variant='body2'>
            Are you sure you want to stop recommending this item?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.primary.main}22`,
            backgroundColor: `${theme.palette.primary.main}08`,
          }}
        >
          <Button
            onClick={() => setStopRecommendConfirmOpen(false)}
            disabled={isSavingMinMax}
            sx={{ color: theme.palette.delete.main }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStopRecommending}
            variant='contained'
            disabled={isSavingMinMax || hasInvalidMinMaxRange}
            sx={{ minWidth: 140, fontWeight: 700 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuggestedOrderTable;
